import { mono, omne, supportive } from "../theme/colors";

export enum PRODUCT_STATUS {
  DRAFT = "draft",
  PENDING_APPROVAL = "pendingApproval",
  APPROVED = "approved",
  REJECTED = "rejected",
  ACTIVE = "active",
  DEACTIVATED = "deactivated",
  COMPLETE = "complete",
  DELETED = "deleted",
}

export enum TRANSLATION_CODE {
  ENGLISH = "en-GB",
  JAPANESE = "ja-JP",
  VIETNAMESE = "vi-VN",
  KHMER = "km-KH",
  INDONESIAN = "id-ID",
  THAI = "th-TH",
  CHINESE_TRADITIONAL = "zh-HK",
  CHINESE_SIMPLIFIED = "zh-CN",
}

export enum COUNTRY {
  NONE = "",
  MALAYSIA = "MY",
  INDONESIA = "ID",
  CAMBODIA = "KH",
  JAPAN = "JP",
  HONG_KONG = "HK",
  PHILIPPINES = "PH",
  SINGAPORE = "SG",
  VIETNAM = "VN",
  THAILAND = "TH",
}

export enum PRODUCT_TYPE {
  NONE = "",
  VOUCHERS = "VOUCHERS",
  DIGITAL_GOODS_AND_SERVICES = "DIGITAL_GOODS_AND_SERVICES",
  PHYSICAL_MERCHANDISE = "PHYSICAL_MERCHANDISE",
}

export enum PAYMENT_TYPE {
  NONE = "",
  FIAT = "FIAT",
  POINTS = "POINTS",
}

export enum PRODUCT_CATEGORY {
  NONE = "",
  REWARD = "Reward",
  CAMPAIGN = "Campaign",
  AUCTION = "Auction",
}

export enum VOUCHER_DISPLAY_TYPE {
  HOTOFFERS = "HotOffers",
  CAROUSEL = "Carousel",
}

export enum VOUCHER_TYPE {
  Code = "Code",
  QRCode = "QRCode",
  // BarCode = "BarCode", Enable later
}

export enum IS_ON_DEMAND {
  On = "On",
  Off = "Off",
}

export enum EXPIRY_DAYS {
  NONE = "",
  THIRTY = "30",
  SIXTY = "60",
  NINETY = "90",
  ONE_TWENTY = "120",
  ONE_EIGHTY = "180",
  THREE_SIXTY = "360",
}

export enum PROMO_TAG {
  HOT = "Hot",
  JUST_FOR_YOU = "JustForYou",
  EXCLUSIVE = "Exclusive",
}

type TranslationFields = {
  bannerHeadline: string;
  displayName: string;
  productDescription: string;
  termsAndConditions: string;
  redemptionInstructionHeader?: string;
  redemptionInstructions?: string;
};

export type ProductDetails = {
  amount: number;
  brandLogo: string;
  brandName: string;
  country: COUNTRY;
  displayTypes: VOUCHER_DISPLAY_TYPE[];
  earliestExpiryDate: string;
  expirationDate: string;
  expiringQuota: number;
  externalProductId: string;
  merchantUrl: string;
  merchantTemplateUrl: string;
  originalPrice: number;
  paymentType: PAYMENT_TYPE;
  productBanner: string;
  productCategory: PRODUCT_CATEGORY;
  productId: string;
  productImage: string;
  productName: string;
  productType: PRODUCT_TYPE;
  promoTag: PROMO_TAG;
  quantity: number;
  redemptionPoints: number;
  redemptionUrlImage: string;
  startDate: string;
  status: PRODUCT_STATUS;
  statusReason: string;
  translationDetails: Partial<Record<TRANSLATION_CODE, TranslationFields>>;
  updatedBy: string;
  updatedDate: string;
  denomination?: number;
  isOnDemand?: IS_ON_DEMAND;
  voucherType?: VOUCHER_TYPE;
  insuranceBidFee?: number;
  noninsuranceBidFee?: number;
  cohortType?: string;
  userLimitFrequency?: string;
  userLimitNumber?: string;
  voucherLimitFrequency?: string;
  voucherLimitNumber?: string;
  pointsFrom?: number;
  pointsTo?: number;
  auctionType?: string;
  voucherExpirationDate?: string;
  httpStatus?: number;
  message?: string;
};

export type ProductRequest = {
  productDetails: Partial<
    Pick<
      ProductDetails,
      | "productId"
      | "externalProductId"
      | "brandLogo"
      | "brandName"
      | "expirationDate"
      | "merchantUrl"
      | "merchantTemplateUrl"
      | "originalPrice"
      | "paymentType"
      | "productBanner"
      | "productImage"
      | "productName"
      | "productType"
      | "promoTag"
      | "startDate"
      | "voucherType"
      | "redemptionUrlImage"
      | "productCategory"
      | "displayTypes"
      | "isOnDemand"
      | "denomination"
      | "insuranceBidFee"
      | "noninsuranceBidFee"
      | "cohortType"
      | "userLimitFrequency"
      | "userLimitNumber"
      | "voucherLimitFrequency"
      | "voucherLimitNumber"
      | "pointsFrom"
      | "pointsTo"
      | "auctionType"
      | "voucherExpirationDate"
    > & {
      promotionalPrice: number;
      productCountry: COUNTRY;
    }
  >;
  translationDetails: Partial<Record<TRANSLATION_CODE, TranslationFields>>;
};

export type ProductInventory = {
  total: number;
  redeemed: number;
  remaining: number;
  expired: number;
  earliestExpiryCount: number;
  earliestExpiryDate: string;
};

export type ProductStatusCount = Record<PRODUCT_STATUS, number>;

export const ProductStatusColour: Record<PRODUCT_STATUS, string> = {
  [PRODUCT_STATUS.DRAFT]: omne.orange.mid,
  [PRODUCT_STATUS.PENDING_APPROVAL]: supportive.light.blue,
  [PRODUCT_STATUS.APPROVED]: mono.grey[2],
  [PRODUCT_STATUS.REJECTED]: supportive.red,
  [PRODUCT_STATUS.ACTIVE]: supportive.lime,
  [PRODUCT_STATUS.DEACTIVATED]: mono.grey[2],
  [PRODUCT_STATUS.COMPLETE]: mono.grey[2],
  [PRODUCT_STATUS.DELETED]: mono.grey[2],
};

export function convertProductDetailsToRequest(
  details: ProductDetails,
): ProductRequest {
  return {
    productDetails: {
      productId: details.productId ?? "",
      externalProductId: details.externalProductId ?? "",
      brandLogo: details.brandLogo ?? "",
      brandName: details.brandName ?? "",
      expirationDate: details.expirationDate ?? "",
      merchantUrl: details.merchantUrl ?? "",
      merchantTemplateUrl: details.merchantTemplateUrl ?? "",
      originalPrice: details.originalPrice ?? undefined,
      paymentType: details.paymentType ?? PAYMENT_TYPE.NONE,
      productBanner: details.productBanner ?? "",
      productCountry: details.country ?? COUNTRY.NONE,
      productImage: details.productImage ?? "",
      productName: details.productName ?? "",
      productType: details.productType ?? PRODUCT_TYPE.NONE,
      promoTag: details.promoTag ?? undefined,
      promotionalPrice: details.paymentType
        ? details.paymentType === PAYMENT_TYPE.FIAT
          ? details.amount
          : details.redemptionPoints
        : undefined,
      startDate: details.startDate ?? "",
      voucherType: details.voucherType ?? undefined,
      displayTypes: [VOUCHER_DISPLAY_TYPE.CAROUSEL],
      redemptionUrlImage: details.redemptionUrlImage ?? "",
      productCategory: details.productCategory ?? "",
      isOnDemand: details.isOnDemand ?? undefined,
      denomination: details.denomination ?? undefined,
      insuranceBidFee: details.insuranceBidFee ?? 0,
      noninsuranceBidFee: details.noninsuranceBidFee ?? 0,
      cohortType: details.cohortType ?? COHORT_TYPE.ALL,
      userLimitFrequency: details.userLimitFrequency ?? undefined,
      userLimitNumber:
        details.userLimitFrequency && details.userLimitNumber
          ? details.userLimitNumber
          : undefined,
      voucherLimitFrequency: details.voucherLimitFrequency ?? undefined,
      voucherLimitNumber:
        details.voucherLimitFrequency && details.voucherLimitNumber
          ? details.voucherLimitNumber
          : undefined,
      pointsFrom: details.pointsFrom ?? undefined,
      pointsTo: details.pointsTo ?? undefined,
      auctionType: details.auctionType ?? "",
      voucherExpirationDate: details.voucherExpirationDate ?? "",
    },
    translationDetails: details.translationDetails,
  };
}

export const isStatusApproved = (prdStatus: PRODUCT_STATUS) =>
  prdStatus === PRODUCT_STATUS.APPROVED;

export enum COHORT_TYPE {
  SPECIFICUSER = "SpecificUser",
  INSURANCE = "Insurance",
  INSURANCESCB = "InsuranceSCB",
  INSURANCEFWD = "InsuranceFWD",
  INACTIVEINSURANCE = "InactiveInsurance",
  NON_INSURANCE = "Noninsurance",
  NON_INSURANCEANDINACTIVEINSURANCE = "NoninsuranceAndInactiveInsurance",
  ALL = "All",
}

export enum LIMIT_FREQUENCY {
  DAILY = "Daily",
  MONTHLY = "Monthly",
  FOREVER = "Forever",
}

export enum AUCTION_TYPE {
  HOURS24 = "TwentyFourHours",
  SPECIFIC = "SpecificPeriod",
}
